'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import { DragSource, DropTarget } from 'react-dnd';
import { throttle } from 'underscore';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIMoveableGroup from 'UIComponents/draggable/UIMoveableGroup';
import { CUSTOM_WIDGET_CHOICE_FIELD_OPTION } from 'DesignData/enums/DragDropItemTypes';
import FormControlWithErrors from 'DesignEditorsUI/components/customWidgetEditor/sidebar/FormControlWithErrors';
import SidebarInput from 'DesignEditorsUI/components/shared/sidebar/SidebarInput';
import { compose } from 'redux';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.fieldEdit.contentOptions.choice.${key}`;

let draggingIndex = null;
/**
 * DragSource
 */

const ChoiceOptionDragSource = DragSource(CUSTOM_WIDGET_CHOICE_FIELD_OPTION, {
  beginDrag(props) {
    draggingIndex = props.index;
    return {
      index: props.index
    };
  },

  isDragging(props) {
    return props.index === draggingIndex;
  }

}, (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  connectDragPreview: connect.dragPreview(),
  isDragging: monitor.isDragging()
}));
/**
 * DropTarget
 */

const ChoiceOptionDropTarget = DropTarget(CUSTOM_WIDGET_CHOICE_FIELD_OPTION, {
  hover: throttle((props, monitor) => {
    draggingIndex = props.index;
    const item = monitor.getItem();

    if (!item || props.index === item.index) {
      return;
    }

    props.onMove(item.index, props.index);
    monitor.getItem().index = props.index;
  }, 250)
}, connect => ({
  connectDropTarget: connect.dropTarget()
}));
const ChoiceOption = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    value,
    label,
    deletable,
    duplicateError,
    onDelete,
    onLabelChange,
    onValueChange,
    connectDropTarget,
    connectDragSource,
    connectDragPreview,
    isDragging,
    onLabelBlur,
    readOnly
  } = props;
  const errors = duplicateError ? [duplicateError] : [];
  const className = 'choice-option' + (isDragging ? " is--dragging" : "");
  return /*#__PURE__*/_jsxs(UIMoveableGroup, {
    className: className,
    closeable: deletable,
    connectDragPreview: connectDragPreview,
    connectDragSource: connectDragSource,
    connectDropTarget: connectDropTarget,
    draggable: !readOnly,
    onClose: onDelete,
    ref: ref,
    children: [/*#__PURE__*/_jsx(UIFormControl, {
      "aria-label": "label",
      children: /*#__PURE__*/_jsx(SidebarInput, {
        className: "choice-label-input",
        onChange: onLabelChange,
        placeholder: I18n.text(i18nKey('placeholders.label')),
        value: label,
        onBlur: onLabelBlur,
        disabled: readOnly
      })
    }), /*#__PURE__*/_jsx(FormControlWithErrors, {
      "aria-label": "value",
      errors: errors,
      children: /*#__PURE__*/_jsx(SidebarInput, {
        className: "is--code",
        onChange: onValueChange,
        placeholder: I18n.text(i18nKey('placeholders.value')),
        value: value,
        disabled: readOnly
      })
    })]
  });
});
ChoiceOption.propTypes = {
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  deletable: PropTypes.bool,
  duplicateError: PropTypes.object,
  isDragging: PropTypes.bool.isRequired,
  label: PropTypes.string,
  onDelete: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired,
  onValueChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  onLabelBlur: PropTypes.func.isRequired,
  readOnly: PropTypes.bool
};
ChoiceOption.defaultProps = {
  closeable: null,
  duplicateError: null,
  label: '',
  value: ''
};
export default compose(ChoiceOptionDragSource, ChoiceOptionDropTarget)(ChoiceOption);